/**
 * Brand mark — official Laimės Dieta logo (2026).
 * The source PNG is black artwork on transparent background.
 * On dark surfaces we wrap it in a cream pill so detail stays readable.
 */
import { cn } from "@/lib/utils";
import logoSrc from "@/assets/laimes-dieta-logo-2026.png";
import humanMostlyLogo from "@/assets/human-mostly-logo.png.asset.json";
import humanMostlyLogoLight from "@/assets/human-mostly-logo-light.png.asset.json";
import { useLang } from "@/i18n";

interface BrandMarkProps {
  className?: string;
  /** Color scheme: 'dark' for cream backgrounds, 'light' for dark backgrounds */
  variant?: "dark" | "light";
  /** Optional size override (tailwind height class). Defaults to responsive 2.4 / 2.7rem */
  sizeClassName?: string;
}

const BrandMark = ({ className, variant = "dark", sizeClassName }: BrandMarkProps) => {
  const { market, t } = useLang();
  const heightClass = sizeClassName ?? "h-[2.4rem] md:h-[2.7rem]";

  if (market === "intl") {
    const intlLogo = variant === "light" ? humanMostlyLogoLight.url : humanMostlyLogo.url;
    return (
      <span className={cn("inline-flex items-center", className)}>
        <img
          src={intlLogo}
          alt={t("brand.name")}
          className={cn(heightClass, "w-auto select-none")}
          draggable={false}
        />
      </span>
    );
  }

  const img = (
    <img
      src={logoSrc}
      alt="Laimės Dieta"
      className={cn(heightClass, "w-auto select-none")}
      draggable={false}
    />
  );

  if (variant === "light") {
    // Wrap in cream pill so the black artwork reads against dark backgrounds.
    return (
      <span className={cn("inline-flex items-center bg-cream rounded-full px-5 py-2", className)}>
        {img}
      </span>
    );
  }

  return <span className={cn("inline-flex items-center", className)}>{img}</span>;
};

export default BrandMark;
